/**
 * Nicotine River — Quickshop
 *
 * Opens a variant picker from any product card and adds to cart using the same
 * contract as Prestige's own product form, so the existing CartSection picks it
 * up and opens the drawer:
 *
 *   POST {routes.cartAddUrl}.js  →  dispatch bubbling 'product:added'
 *
 * Event delegation is required: the collection filter/sort reload replaces the
 * innerHTML of .CollectionInner__Products, which would drop bound handlers.
 */
(function () {
  'use strict';

  var SELECTOR_TRIGGER = '[data-nr-quickshop]';
  var FOCUSABLE = 'a[href], button:not([disabled]), select, input, [tabindex]:not([tabindex="-1"])';

  // Same path as snippets/nr-footer-reviews.liquid. Sized by CSS rather than
  // width/height attributes so one rule controls the row.
  var STAR_SVG =
    '<svg class="nr-reviews__star" viewBox="0 0 18 18" aria-hidden="true" focusable="false">' +
    '<path d="M9 14.118L14.562 17.475L13.086 11.148L18 6.891L11.529 6.342L9 0.375L6.471 6.342L0 6.891L4.914 11.148L3.438 17.475L9 14.118Z" fill="currentColor"/></svg>';

  var root;
  var cache = {};
  var ratings = {};
  var state = { product: null, variant: null, lastFocus: null, open: false };

  /* ---------------------------------------------------------------- money */

  function formatMoney(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var value = (cents / 100).toFixed(2);

    function withDelimiters(num, precision, thousands, decimal) {
      var parts = parseFloat(num).toFixed(precision).split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, token) {
      switch (token) {
        case 'amount': return withDelimiters(value, 2, ',', '.');
        case 'amount_no_decimals': return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
        case 'amount_with_space_separator': return withDelimiters(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return withDelimiters(value, 0, ' ', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(value, 2, "'", '.');
        default: return withDelimiters(value, 2, ',', '.');
      }
    });
  }

  /* ------------------------------------------------------------------ dom */

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function ensureRoot() {
    if (root) return root;

    root = document.querySelector('[data-nr-quickshop-root]');
    if (!root) return null;

    root.addEventListener('click', function (event) {
      if (event.target === root || event.target.closest('[data-nr-quickshop-close]')) {
        close();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (!state.open) return;
      if (event.key === 'Escape') {
        close();
      } else if (event.key === 'Tab') {
        trapFocus(event);
      }
    });

    return root;
  }

  function trapFocus(event) {
    var panel = root.querySelector('[data-nr-quickshop-panel]');
    if (!panel) return;

    var items = Array.prototype.filter.call(
      panel.querySelectorAll(FOCUSABLE),
      function (node) { return node.offsetParent !== null; }
    );
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* -------------------------------------------------------------- variants */

  function optionNames(product) {
    return (product.options || []).map(function (option) {
      return typeof option === 'string' ? option : option.name;
    });
  }

  /* Values are derived from the variants rather than product.options so that
     option shapes across API versions are handled and empty values dropped. */
  function optionValues(product, index) {
    var seen = [];
    (product.variants || []).forEach(function (variant) {
      var value = variant.options ? variant.options[index] : variant['option' + (index + 1)];
      if (value != null && seen.indexOf(value) === -1) seen.push(value);
    });
    return seen;
  }

  function selectedValues() {
    return Array.prototype.map.call(
      root.querySelectorAll('[data-nr-quickshop-option]'),
      function (select) { return select.value; }
    );
  }

  function findVariant(product, values) {
    return (product.variants || []).filter(function (variant) {
      return values.every(function (value, index) {
        var candidate = variant.options ? variant.options[index] : variant['option' + (index + 1)];
        return candidate === value;
      });
    })[0] || null;
  }

  /* ----------------------------------------------------------------- render */

  function render(product) {
    var panel = root.querySelector('[data-nr-quickshop-panel]');
    panel.innerHTML = '';

    var close = el('button', 'nr-qs__close');
    close.type = 'button';
    close.setAttribute('data-nr-quickshop-close', '');
    close.setAttribute('aria-label', 'Close quick shop');
    close.innerHTML = '<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false"><path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round"/></svg>';
    panel.appendChild(close);

    var media = el('div', 'nr-qs__media');
    var img = el('img');
    img.alt = '';
    img.setAttribute('data-nr-quickshop-image', '');
    // 900 rather than 640: the image now fills half the dialog, up to about
    // 450px wide, so 640 was under 1.5x and softened on a retina screen.
    if (product.featured_image) img.src = sized(product.featured_image, 900);
    media.appendChild(img);
    panel.appendChild(media);

    var body = el('div', 'nr-qs__body');

    // No vendor line. It named the upstream manufacturer — Dow above Propylene
    // Glycol, Musim Mas above Vegetable Glycerin — which reads as the brand a
    // buyer is purchasing from and is not what Nicotine River sells against.
    // The product pages have never shown it: show_vendor is off on both product
    // templates, so this was the only place it surfaced.

    var heading = el('h2', 'nr-qs__title');
    var link = el('a', null, product.title);
    link.href = product.url || ('/products/' + product.handle);
    heading.appendChild(link);
    body.appendChild(heading);

    var price = el('p', 'nr-qs__price');
    price.setAttribute('data-nr-quickshop-price', '');
    body.appendChild(price);

    var form = el('div', 'nr-qs__form');
    var names = optionNames(product);

    if (!product.has_only_default_variant) {
      names.forEach(function (name, index) {
        var field = el('div', 'nr-qs__field');
        var id = 'nr-qs-option-' + index;

        var label = el('label', 'nr-qs__label', name);
        label.htmlFor = id;
        field.appendChild(label);

        var select = el('select', 'nr-qs__select');
        select.id = id;
        select.setAttribute('data-nr-quickshop-option', String(index));

        optionValues(product, index).forEach(function (value) {
          var option = el('option', null, value);
          option.value = value;
          select.appendChild(option);
        });

        select.addEventListener('change', function () { syncVariant(); });
        field.appendChild(select);
        form.appendChild(field);
      });
    }

    var qtyField = el('div', 'nr-qs__field nr-qs__field--qty');
    var qtyLabel = el('label', 'nr-qs__label', 'Quantity');
    qtyLabel.htmlFor = 'nr-qs-qty';
    var qty = el('input', 'nr-qs__qty');
    qty.type = 'number';
    qty.id = 'nr-qs-qty';
    qty.min = '1';
    qty.step = '1';
    qty.value = '1';
    qty.setAttribute('data-nr-quickshop-qty', '');
    qtyField.appendChild(qtyLabel);
    qtyField.appendChild(qty);
    form.appendChild(qtyField);

    body.appendChild(form);

    var submit = el('button', 'nr-btn nr-btn--block nr-qs__submit', 'Add to cart');
    submit.type = 'button';
    submit.setAttribute('data-nr-quickshop-submit', '');
    submit.addEventListener('click', addToCart);
    body.appendChild(submit);

    var feedback = el('p', 'nr-qs__feedback');
    feedback.setAttribute('data-nr-quickshop-feedback', '');
    feedback.setAttribute('role', 'status');
    feedback.setAttribute('aria-live', 'polite');
    body.appendChild(feedback);

    var full = el('a', 'nr-qs__full', 'View full product details');
    full.href = product.url || ('/products/' + product.handle);
    body.appendChild(full);

    panel.appendChild(body);
    syncVariant();

    // Star rating sits between the title and the price. That is where the product
    // page puts it (product-meta) and where the cards put it (product-item moved
    // it under the title for the same reason: it qualifies the product name), so
    // a buyer who opens quickshop from a card sees it in the place they just saw
    // it. It also fills the line the vendor used to occupy.
    fillRating(product);
  }

  function sized(url, width) {
    if (!url) return '';
    return url.replace(/(\.(?:png|jpe?g|webp|gif))(\?|$)/i, '_' + width + 'x$1$2');
  }

  /* --------------------------------------------------------------- reviews */

  /* Yotpo's own star widget is not used here. It binds by scanning the document
     at load, and this panel is built long after that, so an injected
     .yotpo-widget-instance stays an empty div unless we reach into the app's
     internals to re-initialise it — undocumented, and it breaks whenever Yotpo
     ships a new widget version.
   *
   * The numbers come from Yotpo's public bottomline endpoint instead — the same
   * figures the card widgets show — and the row is drawn with the site's own star
   * component, the one behind the footer score. Two upsides: the stars on the
   * site stay one implementation in one colour, and a blocked or failed Yotpo
   * request leaves no gap rather than an empty widget shell.
   *
   * api-cdn rather than api.yotpo.com: both answer, but the CDN host is the one
   * built for widget traffic and is not subject to the API rate limit. */
  function loadRating(productId) {
    if (ratings[productId] !== undefined) return Promise.resolve(ratings[productId]);

    var key = window.yotpo_app_key;
    if (!key || !productId) return Promise.resolve(null);

    return fetch('https://api-cdn.yotpo.com/products/' + key + '/' + productId + '/bottomline')
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (json) {
        var line = json && json.response && json.response.bottomline;
        var count = line && line.total_reviews;

        // Nothing worth showing for a product with no reviews yet: an empty star
        // row and "0 Reviews" under the title reads worse than no row at all.
        var data = count ? { score: Number(line.average_score) || 0, count: count } : null;

        ratings[productId] = data;
        return data;
      })
      .catch(function () { return null; });
  }

  function starRow(modifier) {
    var row = el('span', 'nr-reviews__starRow ' + modifier);
    for (var i = 0; i < 5; i++) row.insertAdjacentHTML('beforeend', STAR_SVG);
    return row;
  }

  /* Inserted after the title once the numbers arrive rather than rendered as an
     empty placeholder: the body is a flex column with a gap, so a placeholder
     that stayed empty would leave a phantom gap on every product without
     reviews. The cost is the price row settling down by one line when the row
     lands, which only happens on the first open of a product — the result is
     cached per product id after that. */
  function fillRating(product) {
    loadRating(product.id).then(function (data) {
      if (!data || !state.open || state.product !== product) return;

      var heading = root.querySelector('.nr-qs__title');
      if (!heading || root.querySelector('.nr-qs__rating')) return;

      var wrap = el('div', 'nr-qs__rating');
      var stars = el('span', 'nr-reviews__stars');

      // Percentage, not whole stars, so 4.9 draws as 4.9. Rounded to a tenth
      // because the raw division writes 98.00000000000001% into the attribute.
      stars.style.setProperty(
        '--nr-reviews-fill',
        Math.round((data.score / 5) * 1000) / 10 + '%'
      );
      stars.appendChild(starRow('nr-reviews__starRow--track'));
      stars.appendChild(starRow('nr-reviews__starRow--fill'));

      wrap.appendChild(stars);
      wrap.appendChild(
        el('span', 'nr-qs__ratingCount',
          data.count.toLocaleString() + (data.count === 1 ? ' Review' : ' Reviews'))
      );

      // role=img with a label covering both numbers: the stars carry no text and
      // the score is otherwise only in their fill width.
      wrap.setAttribute('role', 'img');
      wrap.setAttribute(
        'aria-label',
        data.score.toFixed(1) + ' out of 5 stars from ' +
          data.count.toLocaleString() + (data.count === 1 ? ' review' : ' reviews')
      );

      heading.insertAdjacentElement('afterend', wrap);
    });
  }

  function syncVariant() {
    var product = state.product;
    if (!product) return;

    var variant = product.has_only_default_variant
      ? product.variants[0]
      : findVariant(product, selectedValues());

    state.variant = variant;

    var priceNode = root.querySelector('[data-nr-quickshop-price]');
    var submit = root.querySelector('[data-nr-quickshop-submit]');
    var image = root.querySelector('[data-nr-quickshop-image]');
    var feedback = root.querySelector('[data-nr-quickshop-feedback]');

    feedback.textContent = '';
    feedback.className = 'nr-qs__feedback';

    if (!variant) {
      priceNode.textContent = '';
      submit.disabled = true;
      submit.textContent = 'Unavailable';
      return;
    }

    priceNode.innerHTML = '';
    priceNode.appendChild(el('span', 'nr-qs__priceNow', formatMoney(variant.price)));

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      priceNode.appendChild(
        el('span', 'nr-qs__priceWas', formatMoney(variant.compare_at_price))
      );
    }

    if (variant.featured_image && variant.featured_image.src) {
      image.src = sized(variant.featured_image.src, 900);
    }

    submit.disabled = !variant.available;
    submit.textContent = variant.available ? 'Add to cart' : 'Sold out';
  }

  /* ------------------------------------------------------------------ cart */

  function addToCart() {
    var variant = state.variant;
    if (!variant || !variant.available) return;

    var submit = root.querySelector('[data-nr-quickshop-submit]');
    var feedback = root.querySelector('[data-nr-quickshop-feedback]');
    var qtyInput = root.querySelector('[data-nr-quickshop-qty]');
    var quantity = Math.max(1, parseInt(qtyInput.value, 10) || 1);

    submit.disabled = true;
    submit.textContent = 'Adding…';
    feedback.textContent = '';
    feedback.className = 'nr-qs__feedback';

    var cartAddUrl = (window.routes && window.routes.cartAddUrl) || '/cart/add';

    document.documentElement.dispatchEvent(
      new CustomEvent('theme:loading:start', { bubbles: true })
    );

    fetch(cartAddUrl + '.js', {
      body: JSON.stringify({ id: variant.id, quantity: quantity }),
      credentials: 'same-origin',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then(function (response) {
        document.documentElement.dispatchEvent(
          new CustomEvent('theme:loading:end', { bubbles: true })
        );

        if (response.ok) {
          // Prestige's CartSection listens for this and re-renders the cart.
          // It does not open the drawer: that call is commented out in
          // theme.min.js, so quickshop confirms inline instead of closing
          // silently. Staying open also suits buyers adding several SKUs.
          document.dispatchEvent(
            new CustomEvent('product:added', {
              bubbles: true,
              detail: { variant: variant, quantity: quantity }
            })
          );

          var cartUrl = (window.routes && window.routes.cartUrl) || '/cart';

          submit.disabled = false;
          submit.textContent = 'Add to cart';

          feedback.className = 'nr-qs__feedback is-success';
          feedback.textContent = 'Added to cart. ';

          var view = document.createElement('a');
          view.href = cartUrl;
          view.className = 'nr-qs__viewCart';
          view.textContent = 'View cart';
          feedback.appendChild(view);

          return null;
        }

        return response.json().then(function (content) {
          submit.disabled = false;
          submit.textContent = 'Add to cart';
          feedback.textContent =
            (content && (content.description || content.message)) ||
            'This item could not be added to your cart.';
          feedback.className = 'nr-qs__feedback is-error';
        });
      })
      .catch(function () {
        document.documentElement.dispatchEvent(
          new CustomEvent('theme:loading:end', { bubbles: true })
        );
        submit.disabled = false;
        submit.textContent = 'Add to cart';
        feedback.textContent = 'Something went wrong. Please try again.';
        feedback.className = 'nr-qs__feedback is-error';
      });
  }

  /* ------------------------------------------------------------- open/close */

  function open(handle, trigger) {
    if (!ensureRoot()) return;

    state.lastFocus = trigger || document.activeElement;
    state.open = true;

    root.hidden = false;
    root.classList.add('is-open');
    document.documentElement.classList.add('nr-no-scroll');

    var panel = root.querySelector('[data-nr-quickshop-panel]');
    panel.innerHTML = '<p class="nr-qs__loading">Loading…</p>';

    load(handle)
      .then(function (product) {
        if (!state.open) return;
        state.product = product;
        render(product);
        var focusTarget = panel.querySelector('[data-nr-quickshop-option], [data-nr-quickshop-submit]');
        if (focusTarget) focusTarget.focus();
      })
      .catch(function () {
        panel.innerHTML =
          '<p class="nr-qs__loading is-error">This product could not be loaded. ' +
          '<a href="/products/' + handle + '">Open the product page</a> instead.</p>';
      });
  }

  function load(handle) {
    if (cache[handle]) return Promise.resolve(cache[handle]);

    var rootUrl = (window.routes && window.routes.rootUrlWithoutSlash) || '';

    return fetch(rootUrl + '/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('not ok');
        return response.json();
      })
      .then(function (product) {
        cache[handle] = product;
        return product;
      });
  }

  function close() {
    if (!state.open) return;

    state.open = false;
    state.product = null;
    state.variant = null;

    root.classList.remove('is-open');
    document.documentElement.classList.remove('nr-no-scroll');

    window.setTimeout(function () {
      if (!state.open) {
        root.hidden = true;
        root.querySelector('[data-nr-quickshop-panel]').innerHTML = '';
      }
    }, 200);

    if (state.lastFocus && typeof state.lastFocus.focus === 'function') {
      state.lastFocus.focus();
    }
  }

  /* --------------------------------------------------------------- delegate */

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest && event.target.closest(SELECTOR_TRIGGER);
    if (!trigger) return;

    var handle = trigger.getAttribute('data-nr-quickshop');
    if (!handle) return;

    event.preventDefault();
    event.stopPropagation();
    open(handle, trigger);
  });
})();
